import {
  AppBar,
  Breadcrumbs,
  Button,
  Dialog,
  Divider,
  IconButton,
  LinearProgress,
  Link,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Slide,
  Stack,
  Toolbar,
  Tooltip,
  Typography
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { Box, Snackbar, SnackbarContent } from '@mui/material';

import UploadFileIcon from '@mui/icons-material/UploadFile';
import ScheduleIcon from '@mui/icons-material/Schedule';
import SyncAltIcon from '@mui/icons-material/SyncAlt';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DescriptionIcon from '@mui/icons-material/Description';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import GridOnIcon from '@mui/icons-material/GridOn';
import ArticleIcon from '@mui/icons-material/Article';
import FolderIcon from '@mui/icons-material/Folder';
import FolderSharedIcon from '@mui/icons-material/FolderShared';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import SlideshowIcon from '@mui/icons-material/Slideshow';
import PeopleIcon from '@mui/icons-material/People';
import CloudQueueIcon from '@mui/icons-material/CloudQueue';
import StorageIcon from '@mui/icons-material/Storage';

import React, { useEffect, useState, useMemo } from 'react';
import { useGoogleFolderItems, useGoogleRootFolders } from '../../hooks/useGoogleDrive';
import useMigrateStore from '../../store/useMigrate';
import useConnectionStore from '../../store/useConnectors';

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const ROOT = { id: 'root', name: 'My Drive' };

const getIcon = (type, isShared) => {
  switch (type) {
    case 'folder':
      return isShared ? <FolderSharedIcon color="primary" /> : <FolderIcon color="primary" />;
    case 'document':
      return <ArticleIcon sx={{ color: '#1a8cff' }} />;
    case 'presentation':
      return <SlideshowIcon color="warning" />;
    case 'spreadsheet':
      return <GridOnIcon color="success" />;
    case 'pdf':
      return <PictureAsPdfIcon color="error" />;
    default:
      return <InsertDriveFileIcon />;
  }
};

/* ================= Breadcrumb ================= */

function DriveBreadcrumb({ breadcrumb, onNavigate, activeTab }) {
  return (
    <Breadcrumbs>
      {breadcrumb.map((item, index) => {
        const isLast = index === breadcrumb.length - 1;
        let displayName = item.name;
        if (index === 0) {
          if (activeTab === 'shared-with-me') {
            displayName = 'Shared with me';
          } else if (activeTab === 'my-drive') {
            displayName = 'My Drive';
          } else {
            displayName = 'All Files';
          }
        }

        return isLast ? (
          <Typography key={item.id} fontWeight={500}>
            {displayName}
          </Typography>
        ) : (
          <Link key={item.id} underline="hover" sx={{ cursor: 'pointer' }} onClick={() => onNavigate(index)}>
            {displayName}
          </Link>
        );
      })}
    </Breadcrumbs>
  );
}

const SelectButton = ({ onClick, endIcon }) => (
  <Button
    size="small"
    variant="contained"
    sx={{
      fontWeight: 'bold',
    }}
    onClick={onClick}
    endIcon={endIcon}
  >
    Select
  </Button>
);

const ActionMenu = ({ anchorEl, open, onOpen, onClose, onConfirm }) => (
  <>
    <IconButton size="small" onClick={(e) => {
      e.stopPropagation();
      onOpen(e)
    }}>
      <MoreVertIcon />
    </IconButton>

    <Menu
      anchorEl={anchorEl}
      open={open}
      onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
    >
      <MenuItem onClick={(e) => onConfirm(e, "upload")}>
        <ListItemIcon>
          <UploadFileIcon fontSize="small" />
        </ListItemIcon>
        <ListItemText primary="Upload" />
      </MenuItem>

      <MenuItem onClick={(e) => onConfirm(e, "schedule")}>
        <ListItemIcon>
          <ScheduleIcon fontSize="small" />
        </ListItemIcon>
        <ListItemText primary="Schedule" />
      </MenuItem>

      <MenuItem onClick={(e) => onConfirm(e, "autos-chedule")}>
        <ListItemIcon>
          <SyncAltIcon fontSize="small" />
        </ListItemIcon>
        <ListItemText primary="Automatic Migration" />
      </MenuItem>
    </Menu>
  </>
);

/* ================= Row ================= */

function DriveRow({ item, onOpen, onSelect, mode }) {
  const isFolder = item.type === 'folder';
  const isShared = item.shared || item.sharedWithMe;
  const [anchorEl, setAnchorEl] = React.useState(null);
  const open = Boolean(anchorEl);
  const isSource = mode === 'source';
  const isDestination = mode === 'destination';

  const handleOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = (e) => {
    e?.stopPropagation();
    setAnchorEl(null);
  };

  const handleConfirmMigration = (e, param) => {
    e?.stopPropagation();
    setAnchorEl(null);
    onSelect(item, param)
  };

  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={1.5}
      sx={{
        px: 1.5,
        py: 1,
        borderRadius: 1,
        cursor: isFolder ? 'pointer' : 'default',
        '&:hover': { backgroundColor: 'action.hover' }
      }}
      onDoubleClick={(e) => {
        e.stopPropagation();
        if (isFolder) {
          onOpen(item);
        }
      }}
    >
      {getIcon(item.type, isShared)}

      <Stack direction="row" alignItems="center" spacing={1} sx={{ flex: 1, minWidth: 0 }}>
        <Typography noWrap sx={{ fontSize: 14 }} title={item.name}>
          {item.name}
        </Typography>
        {isShared && (
          <Tooltip title={item.sharedWithMe ? 'Shared with me' : 'Shared link'}>
            <PeopleIcon sx={{ fontSize: 16, color: item.sharedWithMe ? 'primary.main' : 'text.secondary' }} />
          </Tooltip>
        )}
      </Stack>

      {!isFolder && isSource && <SelectButton onClick={() => onSelect(item)} endIcon={<DescriptionIcon />} />}

      {!isFolder && isDestination && <ActionMenu 
      anchorEl={anchorEl} 
      open={open} 
      onOpen={handleOpen} 
      onClose={handleClose}
      onConfirm={handleConfirmMigration} />}

      {isFolder && (
        <Stack direction="row" spacing={1}>
          {isSource ? (
            <SelectButton 
            onClick={(e) => {
              e.stopPropagation();
              onSelect(item)
            }} 
            endIcon={<FolderIcon />} />
          ) : (
            <ActionMenu 
            anchorEl={anchorEl} 
            open={open} 
            onOpen={handleOpen} 
            onClose={handleClose}
            onConfirm={handleConfirmMigration} />
          )}
        </Stack>
      )}
    </Stack>
  );
}

/* ================= Main Dialog ================= */

export default function GoogleListItemMigrationDialog({ open, onCancel, dataConnection, onConfirm }) {
  const { provider, activeConnectionId, connectionOwnerName } = useConnectionStore((s) => s);
  const {
    folderMode,
    handleSourceProviderData,
    handleDestinationProviderData
  } = useMigrateStore();
  const [breadcrumb, setBreadcrumb] = useState([ROOT]);
  const [activeTab, setActiveTab] = useState('all');

  const currentFolderId = breadcrumb[breadcrumb.length - 1].id;

  const {
    data: rootItems = [],
    isFetching: isFetchingFolder,
    isError: isRootError,
    error: rootError
  } = useGoogleRootFolders();
  const {
    data: folderItems = [],
    isFetching: isFetchingItemFolder,
    isError: isFolderError,
    error: folderError
  } = useGoogleFolderItems(currentFolderId !== 'root' ? currentFolderId : null);

  const [openError, setOpenError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    const error = rootError || folderError;
    if (!error) return;
    console.log(error);
    setErrorMessage(error?.raw?.message || 'You do not have permission to access this Google Drive');
    setOpenError(true);
  }, [rootError, folderError]);

  const handleClose = () => setOpenError(false);

  // 🔍 FILTER TABS
  const rawItems = currentFolderId === 'root' ? rootItems : folderItems;

  const itemsToRender = useMemo(() => {
    if (currentFolderId === 'root') {
      if (activeTab === 'shared-with-me') {
        return rawItems.filter((item) => item.sharedWithMe === true);
      } else if (activeTab === 'my-drive') {
        return rawItems.filter((item) => !item.sharedWithMe);
      } else {
        return rawItems;
      }
    }
    return rawItems;
  }, [rawItems, currentFolderId, activeTab]);

  const handleOpenFolder = (folder) => {
    setBreadcrumb((prev) => [...prev, { id: folder.id, name: folder.name }]);
  };

  const handleSelectItem = (data, param) => {
    const atributeName = data?.name
    let payload = {
      type: data.type === "folder" ? "folders": "items",
      provider: provider,
      connection_id: activeConnectionId,
      connection_owner_name: connectionOwnerName,
      data: data,
      path_data: [...breadcrumb, {name: atributeName, id: data.id}]
    }

    if (folderMode == 'source') {
      payload = {
        ...payload,
        item_id: data.id
      }
      handleSourceProviderData(payload)
    }
    if (folderMode == 'destination') {
      payload = {
        ...payload,
        folder_id: data.id
      }
      handleDestinationProviderData(payload)
    }

    onConfirm({ payload, param });
  };

  const handleBack = () => {
    if (breadcrumb.length <= 1) return;
    setBreadcrumb((prev) => prev.slice(0, -1));
  };

  const handleBreadcrumbNavigate = (index) => {
    setBreadcrumb((prev) => prev.slice(0, index + 1));
  };

  const isPageLoading = isFetchingFolder || isFetchingItemFolder;

  const sidebarItems = [
    { id: 'all', label: 'All Files', icon: <StorageIcon /> },
    { id: 'my-drive', label: 'My Drive', icon: <CloudQueueIcon /> },
    { id: 'shared-with-me', label: 'Shared with me', icon: <PeopleIcon /> }
  ];

  return (
    <>
      <Snackbar open={openError} onClose={handleClose} autoHideDuration={5000} anchorOrigin={{ vertical: 'top', horizontal: 'right' }}>
        <SnackbarContent
          sx={{
            backgroundColor: '#fff',
            minWidth: 360,
            p: 2,
            boxShadow: 3,
            borderRadius: 1,
            borderLeft: '4px solid #d32f2f'
          }}
          message={
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                width: '100%'
              }}
            >
              <ErrorOutlineIcon
                sx={{
                  mr: 2,
                  fontSize: 28,
                  color: 'error.main'
                }}
              />

              <Box sx={{ flex: 1 }}>
                <Typography fontWeight="bold" color="error.main">
                  Google Drive Error
                </Typography>
                <Typography variant="subtitle2" color="text.primary">
                  {errorMessage || 'Failed to load Google Drive data'}
                </Typography>
              </Box>

              <IconButton size="small" onClick={handleClose} sx={{ ml: 2, color: 'text.secondary' }}>
                <CloseIcon />
              </IconButton>
            </Box>
          }
        />
      </Snackbar>

      <Dialog fullScreen open={open} onClose={onCancel} TransitionComponent={Transition}>
        <AppBar sx={{ position: 'relative' }}>
          <Toolbar>
            <Typography sx={{ ml: 2, flex: 1, fontWeight: 'bold' }} variant="h5">
              Google Drive
            </Typography>
            <Button color="inherit" onClick={onCancel}>
              CLOSE
            </Button>
          </Toolbar>
        </AppBar>

        {/* Outer Layout containing Sidebar & Main Content */}
        <Stack direction="row" sx={{ height: 'calc(100vh - 64px)', bgcolor: 'background.paper' }}>
          {/* Sidebar */}
          <Box
            sx={{
              width: { xs: 80, sm: 240 },
              borderRight: '1px solid',
              borderColor: 'divider',
              py: 3,
              px: { xs: 1, sm: 2 },
              flexShrink: 0,
              display: 'flex',
              flexDirection: 'column',
              gap: 1
            }}
          >
            {sidebarItems.map((item) => {
              const isActive = activeTab === item.id;
              return (
                <Button
                  key={item.id}
                  variant={isActive ? 'contained' : 'text'}
                  color={isActive ? 'primary' : 'inherit'}
                  onClick={() => {
                    setActiveTab(item.id);
                    setBreadcrumb([ROOT]);
                  }}
                  startIcon={item.icon}
                  sx={{
                    justifyContent: { xs: 'center', sm: 'flex-start' },
                    px: { xs: 1, sm: 2 },
                    py: 1.2,
                    borderRadius: 50,
                    textTransform: 'none',
                    fontWeight: isActive ? 600 : 500,
                    fontSize: 14,
                    color: isActive ? '#fff' : 'text.primary',
                    backgroundColor: isActive ? 'primary.main' : 'transparent',
                    '& .MuiButton-startIcon': {
                      marginRight: { xs: 0, sm: 1.5 },
                      marginLeft: 0
                    },
                    '&:hover': {
                      backgroundColor: isActive ? 'primary.dark' : 'action.hover'
                    }
                  }}
                >
                  <Box sx={{ display: { xs: 'none', sm: 'block' } }}>{item.label}</Box>
                </Button>
              );
            })}
          </Box>

          {/* Main Content Area */}
          <Stack sx={{ flex: 1, px: { xs: 2, sm: 4 }, py: 3, overflow: 'hidden' }}>
            {/* Header */}
            <Stack direction="row" alignItems="center" spacing={1} mb={2}>
              <Tooltip title="Back">
                <span>
                  <IconButton size="small" disabled={breadcrumb.length <= 1} onClick={handleBack}>
                    <ArrowBackIcon />
                  </IconButton>
                </span>
              </Tooltip>

              <DriveBreadcrumb breadcrumb={breadcrumb} onNavigate={handleBreadcrumbNavigate} activeTab={activeTab} />
            </Stack>

            {/* Column header */}
            <Stack direction="row" alignItems="center" sx={{ px: 1.5, py: 0.5 }}>
              <Typography sx={{ flex: 1 }} fontWeight={500}>
                Name
              </Typography>
              <Typography sx={{ width: 140, textAlign: 'center' }} fontWeight={500}>
                Modified
              </Typography>
            </Stack>

            <Divider />
            {isPageLoading && (
              <Box
                sx={{
                  width: '100%'
                }}
              >
                <LinearProgress />
              </Box>
            )}

            {/* List */}
            <Stack sx={{ flex: 1, overflowY: 'auto', mt: 1, pr: 1 }}>
              {itemsToRender.map((item) => (
                <DriveRow key={item.id} item={item} mode={folderMode} onSelect={handleSelectItem} onOpen={handleOpenFolder} />
              ))}

              {itemsToRender.length === 0 && (
                <Typography sx={{ textAlign: 'center', mt: 4 }} color="text.secondary">
                  No results found
                </Typography>
              )}
            </Stack>
          </Stack>
        </Stack>
      </Dialog>
    </>
  );
}
